import {Logger} from '@practicegenius/domain/src/common/logger';
import {z} from 'zod';
import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {makeChannelUpdateMessage} from '@practicegenius/domain/src/sites-mobile-app/messages';
import {UseMutationResult} from '@tanstack/react-query';
import {
  inMobileApp,
  parseAppToSitesMessage,
  postMessageToMobileApp,
} from '../utils/MobileApp';
import {isClient} from '../utils/environment';
import {CatastrophicErrorBoundary} from '../components/CatastrophicErrorBoundary';

if (isClient)
  // @ts-expect-error  This global is the only way to opt in to enterprise recaptcha 🤦
  window.recaptchaOptions = {
    useRecaptchaNet: true,
    enterprise: true,
  };

export const recaptchaPlatformSchema = z.enum([
  'web',
  'ios',
  'android',
  'mobile',
]);
export type RecaptchaPlatform = z.infer<typeof recaptchaPlatformSchema>;

export const recaptchaActionSchema = z.enum(['LOGIN']);
export type RecaptchaAction = z.infer<typeof recaptchaActionSchema>;

type RecaptchaHookConfig = {
  logger: Logger;
  /**
   * This mutation is used to setup the session to bypass recaptcha.
   * It is named `syncSession` because this can wind up unobfuscated in client bundles
   *   and we don't want to make it that easy to figure out how to bypass our recaptcha setup.
   *
   * If this is used by a mobile client that doesn't support recaptcha, we have to completely bypass the
   *   web-based recaptcha check because web recaptcha does not work when rendered in a mobile webview.
   */
  useSyncSession: () => UseMutationResult<unknown, Error, void>;
  recaptchaSiteKey?: string;
};

/** Make the hook that provides recaptcha functionality to the client */
export const makeUseRecaptcha =
  ({logger, recaptchaSiteKey, useSyncSession}: RecaptchaHookConfig) =>
  (actionName: string) => {
    const recaptchaRef = React.useRef<ReCAPTCHA>(null);
    const {mutateAsync: syncSession} = useSyncSession();

    // We put this in a ref so we can await it in the getRecaptchaData function just in case the request is slow or the user is super fast
    const supportsRecaptchaRequest = React.useRef<Promise<boolean>>();
    React.useEffect(() => {
      // The promise has to be set in an effect to prvent it from being called multiple times.
      // Directly constructing the promise here will cause it to start immediately.
      supportsRecaptchaRequest.current = new Promise(resolve => {
        if (!inMobileApp) return resolve(true);

        postMessageToMobileApp(makeChannelUpdateMessage('generic', true));
        postMessageToMobileApp({
          type: 'supports_feature',
          feature: 'recaptcha',
        });

        const handleResponse = (event: MessageEvent) => {
          // an unrelated 'recaptcha-setup' message is sent with a string payload
          // so we need to rule out payloads that can't be JSON.parsed
          const data = parseAppToSitesMessage(event.data);
          if (
            data &&
            data.channel === 'generic' &&
            data.type === 'supports_feature' &&
            data.payload === 'recaptcha'
          ) {
            window.removeEventListener('message', handleResponse);
            return resolve(true);
          }
        };

        window.addEventListener('message', handleResponse);

        // If we don't get a response from the mobile app, we'll assume that it doesn't support recaptcha
        setTimeout(() => {
          window.removeEventListener('message', handleResponse);
          return resolve(false);
        }, 5000);

        return () => {
          window.removeEventListener('message', handleResponse);
        };
      });
    }, []);

    return {
      getRecaptchaData: async () => {
        const supportsRecaptcha = await supportsRecaptchaRequest.current;
        return new Promise<{
          recaptchaToken: string;
          platform: RecaptchaPlatform;
        }>(resolve => {
          if (inMobileApp) {
            if (!supportsRecaptcha) {
              return syncSession().then(() => {
                resolve({recaptchaToken: '89120016371', platform: 'mobile'});
              });
            }

            postMessageToMobileApp(makeChannelUpdateMessage('recaptcha', true));
            postMessageToMobileApp({
              type: 'request_recaptcha_token',
              payload: {actionName},
            });
            const handleMessage = (event: MessageEvent) => {
              const data = parseAppToSitesMessage(event.data);
              if (
                data &&
                data.channel === 'recaptcha' &&
                data.type === 'receive_recaptcha_token'
              ) {
                const {recaptchaToken, platform} = data.payload;
                resolve({recaptchaToken, platform});
              }
            };

            window.addEventListener('message', handleMessage);
          } else {
            const ref = recaptchaRef.current;

            /*
             * If the user can submit before this mounts, then the check will fail,
             * but they are also arguably non-human... 🤖
             */
            if (
              !ref ||
              // @ts-expect-error  This is a private method, but it's the only way to get the recaptcha token
              !ref.getCaptchaFunction ||
              // @ts-expect-error  This is a private method, but it's the only way to get the recaptcha token
              !ref.getCaptchaFunction('execute')
            ) {
              logger.error(
                'Attempted Recaptcha token request before it was ready',
              );
              return resolve({
                recaptchaToken: '',
                platform: 'web' as const,
              });
            }

            const formId = ref.getWidgetId();

            /*
             * Unfortunately, this dangerously undocumented method is the only way to
             * appropriately utilize Enterprise ReCAPTCHA.  This version relies upon
             * the "action", which is not yet available in this library.
             *
             * @see https://github.com/dozoisch/react-google-recaptcha/issues/289
             */
            ref
              // @ts-expect-error  See above
              .getCaptchaFunction('execute')(formId, {
                action: actionName,
              })
              .then((res: string) => {
                resolve({
                  recaptchaToken: res,
                  platform: 'web',
                });
              });
          }
        });
      },
      RecaptchaComponent:
        inMobileApp || !recaptchaSiteKey ? null : (
          <CatastrophicErrorBoundary
            logger={logger}
            errorNode={null}
            errorLogMessage="Error rendering ReCAPTCHA"
          >
            <style>
              {`
          .grecaptcha-badge { visibility: hidden; }
        `}
            </style>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              size="invisible"
            />
          </CatastrophicErrorBoundary>
        ),
    };
  };
